import React, { useState, useEffect } from 'react'
import BigNumber from 'bignumber.js'
import { Flex, Text } from '@pancakeswap/uikit'
import styled from 'styled-components'
import { BIG_ZERO } from 'utils/bigNumber'
import { useTranslation } from 'react-i18next'
import { LotteryRound } from 'state/types'
import { RewardBracketDetail } from './RewardBracketDetail'

const Wrapper = styled(Flex)`
  width: 100%;
  flex-direction: column;
`

const RewardsInner = styled.div`
  display: grid;
  grid-template-columns: repeat(2, auto);
  row-gap: 16px;

  ${({ theme }) => theme.mediaQueries.sm} {
    grid-template-columns: repeat(4, 1fr);
  }
`

interface RewardMatchesProps {
  lotteryNodeData: LotteryRound
  isHistoricRound?: boolean
}

interface RewardsState {
  isLoading: boolean
  cakeToBurn: BigNumber
  rewardsLessTreasuryFee: BigNumber
  rewardsBreakdown: string[]
  ticketRewardsBreakdown: string[]
  countWinnersPerBracket: string[]
}

const RewardBrackets: React.FC<RewardMatchesProps> = ({ lotteryNodeData, isHistoricRound }) => {
  const { t } = useTranslation()
  const [state, setState] = useState<RewardsState>({
    isLoading: true,
    cakeToBurn: BIG_ZERO,
    rewardsLessTreasuryFee: BIG_ZERO,
    rewardsBreakdown: null,
    ticketRewardsBreakdown: null,
    countWinnersPerBracket: null,
  })

  useEffect(() => {
    if (lotteryNodeData) {
      const { treasuryFee, amountCollectedInCake, rewardsBreakdown, countWinnersPerBracket, ticketRewardsBreakdown } =
        lotteryNodeData

      const feeAsPercentage = new BigNumber(treasuryFee).div(100)
      const cakeToBurn = feeAsPercentage.div(100).times(new BigNumber(amountCollectedInCake))
      const amountLessTreasuryFee = new BigNumber(amountCollectedInCake).minus(cakeToBurn)
      setState({
        isLoading: false,
        cakeToBurn,
        rewardsLessTreasuryFee: amountLessTreasuryFee,
        rewardsBreakdown,
        countWinnersPerBracket,
        ticketRewardsBreakdown,
      })
    } else {
      setState({
        isLoading: true,
        cakeToBurn: BIG_ZERO,
        rewardsLessTreasuryFee: BIG_ZERO,
        rewardsBreakdown: null,
        countWinnersPerBracket: null,
        ticketRewardsBreakdown: null,
      })
    }
  }, [lotteryNodeData])

  const getCakeRewards = (bracket: number) => {
    const shareAsPercentage = new BigNumber(state.rewardsBreakdown[bracket]).div(100)
    return state.rewardsLessTreasuryFee.div(100).times(shareAsPercentage)
  }

  const { isLoading, countWinnersPerBracket, ticketRewardsBreakdown, cakeToBurn } = state
  const totalNftReward = ticketRewardsBreakdown?.reduce(
    (sum, n, i) => sum + Number(n) * Number(countWinnersPerBracket[i]),
    0,
  )
  const rewardBrackets = [0, 1, 2, 3, 4, 5]

  return (
    <Wrapper>
      <Text color="#fff" fontSize="14px" mb="24px">
        {t('views.lottery.components.rewardBracket.text1')}{' '}
        {!isHistoricRound && t('views.lottery.components.rewardBracket.text2')}
      </Text>
      <RewardsInner>
        {rewardBrackets.map((bracketIndex) => (
          <RewardBracketDetail
            key={bracketIndex}
            rewardBracket={bracketIndex}
            rewardNftAmount={ticketRewardsBreakdown ? ticketRewardsBreakdown[bracketIndex] : null}
            cakeAmount={!isLoading && getCakeRewards(bracketIndex)}
            numberWinners={!isLoading && countWinnersPerBracket[bracketIndex]}
            isHistoricRound={isHistoricRound}
            isLoading={isLoading}
          />
        ))}
        <RewardBracketDetail rewardBracket={0} cakeAmount={cakeToBurn} isBurn isLoading={isLoading} />
        {isHistoricRound && (
          <RewardBracketDetail
            rewardBracket={0}
            cakeAmount={cakeToBurn}
            nftAmount={totalNftReward}
            isNFT
            isLoading={isLoading}
          />
        )}
      </RewardsInner>
    </Wrapper>
  )
}

export default RewardBrackets
